// import { WarningFilled } from "@ant-design/icons";
import styles from "./LadleHealtProcessSummary.module.less";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Button, Typography } from "antd";
import { HealtProcessSummaryResponseParameters } from "~/pages/copilotMessage/types/copilot";
import ProgressDetail from "~/components/progressDetail/ProgressDetail";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ThicknessProjectedLine } from "./components/ThicknessProjectedLine";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import TypeComponentCard from "../typeComponentCard/TypeComponentCard";
import { getRepairList } from "../../helpers/chartHelper";
import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import ProgressLowHighLimit from "~/components/progressLowHighLimit/ProgressLowHighLimit";
import { useUserLogContext } from "~/providers/UserLogProvider";
import { CopilotLogsType } from "~/pages/copilotMessage/types/copilotLogs";

interface LadleHealthProcessSummaryPropss {
  parameters: HealtProcessSummaryResponseParameters;
}

function LadleHealtProcessSummary({
  parameters,
}: LadleHealthProcessSummaryPropss) {
  const { insertUserLog } = useUserLogContext();
  const explainContainerRef = useRef<HTMLDivElement>(null);
  const explainContainerTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [detailsVisible, setDetailsVisible] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isProjectedLineFullScreen, setIsProjectedLineFullScreen] =
    useState(false);

  const {
    current,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    predicted,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    max,
    risks,
    repairs,
    totalContactTime,
    currentCumulativeEtto,
    warningThreshold,
    startPoint,
    endPoint,
    mean,
    ettoList,
  } = useMemo(() => {
    const {
      CleanedFilteredPredictedThickness,
      PredictedHeatNumberLe,
      PredictedHeatNumberMe,
    } = parameters.MinCourseClockThicknessAlgorithmResponse;

    const curr = Math.max(
      ...Object.keys(CleanedFilteredPredictedThickness).map((x) => Number(x)),
    );

    const repairs = getRepairList(
      parameters.MinCourseClockThicknessAlgorithmResponse
        .SlagReplacementHeatNumbers,
    );

    //INFO: Fatih,
    //If prediction is over, predictedHeatNumberLe and predictedHeatNumberMe are -1 and breaks the system
    return {
      current: curr,
      max: curr + (PredictedHeatNumberLe === -1 ? 0 : PredictedHeatNumberLe),
      predicted:
        curr + (PredictedHeatNumberMe === -1 ? 0 : PredictedHeatNumberMe),
      risks: parameters.AlarmsAndCriticalGrades.CriticalGradeHeatNumbers,
      totalContactTime:
        parameters.AlarmsAndCriticalGrades.MessageForProcessParametres
          ?.CurrentCumulativeEtto || 0,
      repairs,
      currentCumulativeEtto:
        parameters.AlarmsAndCriticalGrades.MessageForProcessParametres
          ?.CurrentCumulativeEtto,
      warningThreshold:
        parameters.AlarmsAndCriticalGrades.MessageForProcessParametres
          ?.WarningThreshold,
      startPoint:
        parameters.AlarmsAndCriticalGrades.MessageForProcessParametres
          ?.StartPoint,
      endPoint:
        parameters.AlarmsAndCriticalGrades.MessageForProcessParametres
          ?.EndPoint,
      mean: parameters.AlarmsAndCriticalGrades.MessageForProcessParametres
        ?.Mean,
      ettoList:
        parameters.AlarmsAndCriticalGrades.HeatEttosAndLongRunningHeats?.filter(
          (x) => x.Etto > 180 && x.HeatNumber > 0,
        )?.map((x) => ({ heatNumber: x.HeatNumber, etto: x.Etto })) || [],
    };
  }, [
    parameters.MinCourseClockThicknessAlgorithmResponse,
    parameters.AlarmsAndCriticalGrades.CriticalGradeHeatNumbers,
    parameters.AlarmsAndCriticalGrades.MessageForProcessParametres,
    parameters.AlarmsAndCriticalGrades.HeatEttosAndLongRunningHeats,
  ]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const showDetails = useCallback(() => {
    explainContainerTimeoutRef.current = setTimeout(() => {
      const explainContainer = explainContainerRef.current;
      if (explainContainer) {
        explainContainer.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
    insertUserLog(CopilotLogsType.ExplainDetailsClicked);
    setDetailsVisible((v) => !v);
  }, [insertUserLog]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const CampaignInfoSubTitle = useMemo(() => {
    const { CriticalCourseClock } =
      parameters.MinCourseClockThicknessAlgorithmResponse;

    return (
      <div>
        {isProjectedLineFullScreen && (
          <div style={{ display: "flex", gap: "10px" }}>
            <Typography.Text>{`Campaign: ${parameters.CampaignHeat?.CampaignName}`}</Typography.Text>
            <Typography.Text>{`Heat: ${parameters.CampaignHeat?.HeatNumber} ${parameters.CampaignHeat?.HeatName}`}</Typography.Text>
          </div>
        )}

        <div style={{ display: "flex", gap: "4px", fontStyle: "italic" }}>
          <Typography.Text>{`* Below chart shows the refractory thickness analysis for the most critical zone:`}</Typography.Text>
          <Typography.Text
            style={{ fontWeight: 600 }}
          >{`Course ${CriticalCourseClock.Course}, Clock ${CriticalCourseClock.Clock}.`}</Typography.Text>
        </div>
      </div>
    );
  }, [parameters, isProjectedLineFullScreen]);

  useEffect(() => {
    return () => {
      if (explainContainerTimeoutRef.current) {
        clearTimeout(explainContainerTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className={styles.LadleHealtProcessSummary}>
      <div className={styles.infoContainer}>
        <div className={styles.performanceWrapper}>
          <Typography.Text className={styles.performanceTitle}>
            Predicted Slagline Performance
          </Typography.Text>

          {/* TODO: 15221 - Temporary message for hidden prediction features */}
          <div className={styles.mikeMessageContainer}>
            <img
              src="/images/mike.opac.png"
              alt="Mike Assistant"
              className={styles.mikeImage}
            />
            <Typography.Text className={styles.mikeText}>
              "I'm improving how I predict remaining heats by including more
              factors. For now, I've hidden the estimate to avoid misleading
              you. I'll be back soon!"
            </Typography.Text>
          </div>

          <div className={styles.progressBarContainer}>
            <ProgressDetail
              current={current}
              // TODO: 15221 - Temporarily limit max to current to hide Future Heat and Estimated Campaign End boxes
              // max={max}
              // predicted={predicted}
              max={current}
              predicted={current}
              risk={risks}
              repair={repairs}
              ettoList={ettoList}
            />
          </div>
        </div>

        <div className={styles.totalContactTimeWrapper}>
          <Typography.Text className={styles.title}>
            Total Contact Time
          </Typography.Text>

          <Typography.Text
            className={styles.value}
          >{`${totalContactTime.toFixed(0)} min`}</Typography.Text>

          <div className={styles.progressTotalContactContainer}>
            <ProgressLowHighLimit
              start={startPoint}
              end={endPoint}
              current={currentCumulativeEtto}
              warning={warningThreshold}
              mean={mean}
              showDivider={false}
              showLabel
            />
          </div>
        </div>

        {parameters?.ReladleCounts && (
          <div className={styles.reladlingWrapper}>
            <Typography.Text className={styles.title}>
              Re-ladling
            </Typography.Text>

            <div className={styles.reLadlingIconGroupContainer}>
              <div className={styles.reLadlingIconContainer}>
                <UploadOutlined style={{ fontSize: 20, flex: 1 }} />
                <Typography.Text style={{ flex: 2 }}>From:</Typography.Text>
                <Typography.Text style={{ flex: 1 }}>
                  {parameters?.ReladleCounts?.FromCampaign}
                </Typography.Text>
              </div>
              <div className={styles.reLadlingIconContainer}>
                <DownloadOutlined style={{ fontSize: 20, flex: 1 }} />
                <Typography.Text style={{ flex: 2 }}>To:</Typography.Text>
                <Typography.Text style={{ flex: 1 }}>
                  {parameters?.ReladleCounts?.ToCampaign}
                </Typography.Text>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* TODO: 15221 - Temporarily disabled Explain Details button and chart section. Will be re-enabled when DS team improves estimations.
      <div className={styles.explainContainer} ref={explainContainerRef}>
        <Button className={styles.explainButton} onClick={showDetails}>
          <Typography.Title level={3}>
            {detailsVisible ? "Hide Details" : "Explain Details"}
          </Typography.Title>
        </Button>

        {detailsVisible && (
          <div className={styles.chartContainer}>
            <TypeComponentCard
              title={"Predicted Slagline Performance"}
              subTitle={CampaignInfoSubTitle}
              onShowFullScreen={setIsProjectedLineFullScreen}
            >
              <ThicknessProjectedLine
                data={parameters}
                isExpanded={isProjectedLineFullScreen}
              />
            </TypeComponentCard>
          </div>
        )}
      </div>
      */}
    </div>
  );
}

export default LadleHealtProcessSummary;
